import moment from 'moment';
import { checkChimeTransport } from '@/features/diagnostics/services/ChimeReadinessService';
import { CHECK_STATUS, createEmptyVideoCall } from '@/features/diagnostics/types';

const callApi = (invoke, timeoutMs, signal, onLateResponse) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let timer;
    const finish = (error, data, response) => {
      if (settled) {
        if (!error && onLateResponse) onLateResponse(response?.body ?? data);
        return;
      }
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      if (error) reject(error);
      else if ((response?.body ?? data)?.status === 'Failure') reject(new Error('RequestFailed'));
      else resolve(response?.body ?? data);
    };
    const abort = () => finish(new Error('Cancelled'));
    if (signal?.aborted) {
      abort();
      return;
    }
    signal?.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => finish(new Error('TimedOut')), timeoutMs);
    try {
      invoke(finish);
    } catch {
      finish(new Error('RequestFailed'));
    }
  });

const eventTimestamp = (body) => {
  const raw = body?.contacts?.find(
    (contact) => contact?.info?.calendar?.schedule_event?.event_timestamp
  )?.info?.calendar?.schedule_event?.event_timestamp;
  const date = /^\d+$/.test(String(raw)) ? new Date(Number(raw)) : new Date(raw);
  if (!raw || Number.isNaN(date.getTime())) throw new Error('InvalidEventTimestamp');
  return date.toISOString();
};

const buildEvent = (useruuid) => {
  const start = moment();
  const end = start.clone().add(1, 'hour');
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = Intl.DateTimeFormat(navigator.language, { timeZone, timeZoneName: 'longOffset' })
    .formatToParts()
    .find((part) => part.type === 'timeZoneName')?.value;
  return {
    createEvent: {
      contacts: [{ useruuid }],
      event_name: 'HomeSight Network Diagnostic',
      event_description: 'Automated network diagnostic',
      event_type: 'schedule',
      reminder_minutes: 0,
      snoozable: false,
      event_startdate: start.format('YYYY-MM-DD'),
      event_enddate: end.format('YYYY-MM-DD'),
      event_starttime: start.format('HH:mm'),
      event_endtime: end.format('HH:mm'),
      timezone: offset ? `${timeZone} (${offset})` : timeZone,
    },
  };
};

export const runVideoCallSetupCheck = async ({
  scheduleApi,
  videoCallApi,
  currentUseruuid,
  timeoutMs,
  readinessTimeoutMs,
  signal,
  onTeardownStarted,
}) => {
  const result = createEmptyVideoCall();
  result.step = 'create';
  let createRequested = false;
  const cancelledEvents = new Set();
  const cancelEvent = async (eventuuid) => {
    if (cancelledEvents.has(eventuuid)) return;
    cancelledEvents.add(eventuuid);
    try {
      await callApi(
        (callback) => scheduleApi.cancelEvent(currentUseruuid, eventuuid, {}, callback),
        timeoutMs
      );
      result.eventDeleted = true;
    } catch {
      result.eventDeleted = false;
      result.cleanupErrors.push('EventCleanupFailed');
      console.error('Network diagnostic event cancellation failed');
    }
  };

  try {
    if (!scheduleApi || !videoCallApi || !currentUseruuid) throw new Error('APIsUnavailable');
    let createCompleted = false;
    const payload = buildEvent(currentUseruuid);
    const body = await callApi(
      (callback) => {
        createRequested = true;
        scheduleApi.createEvent(currentUseruuid, payload, callback);
      },
      timeoutMs,
      signal,
      (lateBody) => {
        if (!createCompleted && lateBody?.schedule_eventuuid) {
          result.meetingId = lateBody.schedule_eventuuid;
          void cancelEvent(lateBody.schedule_eventuuid);
        }
      }
    );
    createCompleted = true;
    if (!body?.schedule_eventuuid) throw new Error('InvalidEventResponse');
    result.meetingId = body.schedule_eventuuid;
    result.step = 'join';
    const joined = await callApi(
      (callback) =>
        videoCallApi.joinSession(
          { meeting_id: result.meetingId, event_timestamp: eventTimestamp(body) },
          callback
        ),
      timeoutMs,
      signal
    );
    const chime = joined?.chime;
    if (!chime?.Meeting?.MeetingId || !chime?.Attendee?.AttendeeId || !chime?.Attendee?.JoinToken) {
      throw new Error('InvalidChimeResponse');
    }
    result.mediaRegion = chime.Meeting.MediaRegion || null;
    for (const transport of ['udp', 'tcp']) {
      result.step = transport;
      result[transport] = await checkChimeTransport({
        chime,
        transport,
        timeoutMs: readinessTimeoutMs,
        signal,
        cleanupErrors: result.cleanupErrors,
        onTeardownStarted,
      });
      if (signal?.aborted) throw new Error('Cancelled');
      if (result.cleanupErrors.includes(`${transport}:CleanupUnconfirmed`)) {
        if (transport === 'udp') {
          result.tcp = { status: CHECK_STATUS.FAIL, feedback: 'PreviousTransportCleanupUnconfirmed' };
        }
        break;
      }
    }
    result.step = 'done';
  } catch (error) {
    console.warn('Network diagnostic could not complete', result.step);
    // Never expose raw generated-client errors: they can contain response bodies and tokens.
    const knownErrors = [
      'Cancelled', 'TimedOut', 'InvalidEventTimestamp', 'InvalidEventResponse',
      'InvalidChimeResponse', 'APIsUnavailable', 'RequestFailed',
    ];
    result.reason = signal?.aborted
      ? 'Cancelled'
      : knownErrors.includes(error?.message) ? error.message : 'SetupFailed';
    for (const transport of ['udp', 'tcp']) {
      if (result[transport].status === CHECK_STATUS.SKIPPED) {
        result[transport] = { status: CHECK_STATUS.FAIL, feedback: result.reason };
      }
    }
  } finally {
    if (result.meetingId) await cancelEvent(result.meetingId);
    else if (createRequested && result.step === 'create') {
      result.cleanupErrors.push('EventCreationUnconfirmed');
    }
  }
  return result;
};
